/* eslint-disable import/no-duplicates */
import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { type RootState } from 'store/rootReducer';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { colorTheme } from 'theme';
import { CustomDataGrid, DialogBox } from 'components';
import { type GridRowData } from 'components/customDataGrid/CustomDataGrid';
import EditIcon from '@mui/icons-material/Edit';
import Buttons from 'components/elements/Buttons';

import { TextField } from '@mui/material';
import { formatDate } from 'utils/GeneralUtil';
import { Grid } from '@mui/material';
import {
  getAllActiveConfiguration,
  updateActiveConfiguration,
} from 'service/ConfigurationService';
import { setConfiguration } from 'store/configuration';

interface FlexVariable {
  id: number;
  variable: string;
  value: string;
  validFrom: string | null;
  validThru: string | null;
  isValid: boolean;
  information: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Props {
  data: FlexVariable[];
}

const Configuration: React.FC<Props> = () => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const dispatch = useDispatch();

  const [editRowId, setEditRowId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<FlexVariable>>({});
  const [data, setData] = useState<FlexVariable[]>([]);
  const { configuration } = useSelector(
    (state: RootState) => state.configuration,
  );

  useEffect(() => {
    setData(configuration ?? []);
  }, [configuration]);

  useEffect(() => {
    void getData();
  }, []);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getData = async () => {
    const configs: unknown = await getAllActiveConfiguration();
    dispatch(setConfiguration(configs?.data || []));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = (): void => {
    setEditRowId(null);
  };

  const handleSave = async (): void => {
    const { information, value, validFrom, validThru, variable } = formData;

    await updateActiveConfiguration(Number(formData.id), {
      information,
      value,
      validFrom,
      validThru,
      variable,
    });
    setEditRowId(null);
    void getData();
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'variable',
      flex: 2,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Variable
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.variable}
        </Typography>
      ),
    },
    {
      field: 'value',
      flex: 2,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Value
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.value}
        </Typography>
      ),
    },
    {
      field: 'validFrom',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Valid From
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {formatDate(row?.validFrom)}
        </Typography>
      ),
    },
    {
      field: 'validThru',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Valid Thru
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {formatDate(row.validThru)}
        </Typography>
      ),
    },
  ];

  return (
    <Box p={3}>
      <CustomDataGrid
        title="Configuration"
        getRowId={(row: GridRowData) => `${String(row.id)}`}
        columns={columns}
        rows={data as GridRowData[]}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        actionBtnText1="edit"
        ActionBtnIcon1={() => <EditIcon />}
        onActionBtnClick1={(row, event) => {
          event.stopPropagation();
          setEditRowId(row.id);
          setFormData(row);
        }}
      />

      <DialogBox
        open={editRowId !== null}
        onClose={handleClose}
        title={`Edit ${String(formData?.variable ?? '')}`}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              name="value"
              label="Value"
              value={formData.value ?? ''}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              name="validFrom"
              label="Valid From"
              type="date"
              value={formData.validFrom ?? ''}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              name="validThru"
              label="Valid Thru"
              type="date"
              value={formData.validThru ?? ''}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Grid
              container
              direction="row"
              spacing={2}
              sx={{
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <Buttons
                onClick={handleClose}
                variant="contained"
                sx={{
                  m: 1,
                  bgcolor: colors.gray[900],
                  borderRadius: 2,
                }}
              >
                <Typography
                  component="h1"
                  variant="h5"
                  style={{ fontSize: '12px', color: colors.primaryBg[700] }}
                >
                  Cancel
                </Typography>
              </Buttons>
              <Buttons
                onClick={handleSave}
                variant="contained"
                sx={{
                  m: 1,
                  bgcolor: colors.greenAccent[500],
                  borderRadius: 2,
                }}
              >
                <Typography
                  component="h1"
                  variant="h5"
                  style={{ fontSize: '12px' }}
                >
                  Update
                </Typography>
              </Buttons>
            </Grid>
          </Grid>
        </Grid>
      </DialogBox>
    </Box>
  );
};

export default Configuration;
